import React, { memo, useCallback, useRef, useState, useMemo, useEffect } from 'react';
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  Panel,
  ReactFlowProvider,
  ConnectionMode,
} from 'reactflow';
import 'reactflow/dist/style.css';
import CanvasBackground from './CanvasBackground';
import CanvasBackgroundSelector from './CanvasBackgroundSelector';
import { LAYOUT_V2 } from '../../layout/layoutV2';
import useCanvasBgStore from '../../store/canvasBgStore';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';
import McpRunOverlay from './McpRunOverlay';
import CanvasSection from './CanvasSection';
import { useSectionsStore, applySectionMoves } from '../../store/sectionsStore';
import { sectionsToNodes, splitChanges, isSectionId, boxAround } from '../../lmx/trabajos';
import WorkflowRevealOverlay from './WorkflowRevealOverlay';

import { nodeTypes } from '../nodes';
import { useWorkflowStore } from '../../store/workflowStore';
import { useBatchStore } from '../../store/batchStore';
import { useRevealStore } from '../../store/revealStore';
import SourcePickerModal from './SourcePickerModal';
import GallerySelectModal from './GallerySelectModal';
import EmptyCanvasTemplates from './EmptyCanvasTemplates';
import NodeSearchPalette from './NodeSearchPalette';
import useCanvasNavStore, { PATH_HIGHLIGHT_AVAILABLE } from '../../store/canvasNavStore';
import { chainOf } from '../../utils/graphPaths';
import { Images, Library, Route, Search, SquareDashed } from 'lucide-react';
import '../../styles/canvas-nav.css';
import '../../styles/node-capture.css';

// Envío LMX-T · los recuadros de sección se dibujan como un tipo de nodo
// más de React Flow, pero NO están en el store del flujo.
const flowNodeTypes = { ...nodeTypes, canvasSection: CanvasSection };

/** Píxeles que el dock de LMX tapa por abajo (lo publica LmxDock en :root). */
const dockLift = () => parseFloat(getComputedStyle(document.documentElement)
  .getPropertyValue('--lmx-dock-lift')) || 0;

const Canvas = memo(() => {
  const reactFlowWrapper = useRef(null);
  const [reactFlowInstance, setReactFlowInstance] = useState(null);

  const nodes = useWorkflowStore((state) => state.nodes);
  const edges = useWorkflowStore((state) => state.edges);
  const onNodesChange = useWorkflowStore((state) => state.onNodesChange);
  const onEdgesChange = useWorkflowStore((state) => state.onEdgesChange);
  const onConnect = useWorkflowStore((state) => state.onConnect);
  const setSelectedNode = useWorkflowStore((state) => state.setSelectedNode);
  const addNode = useWorkflowStore((state) => state.addNode);
  const validateConnection = useWorkflowStore((state) => state.validateConnection);

  const { authFetchJson } = useAuthenticatedFetch();

  // Fondo de plataforma: lo fija el admin y es el default para todos los
  // usuarios que nunca personalizaron el suyo.
  useEffect(() => {
    authFetchJson('/api/platform/background')
      .then((d) => { if (d?.url) useCanvasBgStore.getState().applyPlatformDefault(d); })
      .catch(() => { /* sin fondo de plataforma configurado */ });
  }, [authFetchJson]);

  // Batch mode overlay — decorate node classNames without mutating store state.
  const batchMode = useBatchStore((s) => s.batchMode);
  const batchStart = useBatchStore((s) => s.startNodeId);
  const batchEnd = useBatchStore((s) => s.endNodeId);
  const batchInvolved = useBatchStore((s) => s.involvedNodeIds);
  const pickingMode = useBatchStore((s) => s.pickingMode);

  // Revelado didáctico del workflow (capa visual: ver store/revealStore.js).
  const revealActive = useRevealStore((s) => s.active);
  const hiddenNodeIds = useRevealStore((s) => s.hiddenNodes);
  const hiddenEdgeIds = useRevealStore((s) => s.hiddenEdges);

  // E5 · navegación: caminos iluminados y "llevame al nodo".
  const selectedNode = useWorkflowStore((s) => s.selectedNode);
  const pathHighlight = useCanvasNavStore((s) => s.pathHighlight);
  const togglePathHighlight = useCanvasNavStore((s) => s.togglePathHighlight);
  const flashNodeId = useCanvasNavStore((s) => s.flashNodeId);
  const setSearchOpen = useCanvasNavStore((s) => s.setSearchOpen);

  // La cadena se calcula en memoria (misma regla que el subgrafo del
  // backend): con 90 nodos es instantáneo y no se le pide nada al server.
  const chain = useMemo(() => {
    if (!PATH_HIGHLIGHT_AVAILABLE || !pathHighlight || !selectedNode?.id) return null;
    return chainOf(selectedNode.id, edges);
  }, [pathHighlight, selectedNode, edges]);

  // Esc limpia la selección (y con ella el resaltado).
  useEffect(() => {
    const onKey = (event) => {
      if (event.key === 'Escape') setSelectedNode(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [setSelectedNode]);

  // "Llevame al nodo" pedido desde afuera (ej. un clip del timeline):
  // se centra recién cuando el canvas está montado y con instancia lista.
  const focusRequest = useCanvasNavStore((s) => s.focusRequest);
  const clearFocusRequest = useCanvasNavStore((s) => s.clearFocusRequest);
  useEffect(() => {
    if (!focusRequest?.nodeId || !reactFlowInstance) return;
    const node = nodes.find((n) => n.id === focusRequest.nodeId);
    if (node) {
      // El dock de LMX tapa la parte de abajo: el centro se corre para que el
      // nodo quede en el área visible.
      const zoom = 1.1;
      reactFlowInstance.setCenter(
        (node.position?.x || 0) + (node.width || 320) / 2,
        (node.position?.y || 0) + (node.height || 200) / 2 + (dockLift() / 2) / zoom,
        { zoom, duration: 500 },
      );
    }
    clearFocusRequest();
  }, [focusRequest, reactFlowInstance, nodes, clearFocusRequest]);

  // Al abrir un proyecto, encuadrar TODO el grafo una sola vez (los proyectos
  // muy grandes quedaban con nodos fuera de la vista inicial).
  const currentProjectId = useWorkflowStore((s) => s.currentProjectId);
  // I-48b · el panel de funciones (Galería, Fondo dinámico) copia la medida
  // REAL de los controles de zoom y se apoya justo encima, con separación:
  // mismo ancho de botón, sin tocarse, aunque cambie el estilo de los controles.
  useEffect(() => {
    if (!LAYOUT_V2) return undefined;
    const root = reactFlowWrapper.current;
    if (!root || typeof ResizeObserver === 'undefined') return undefined;
    let ro = null;
    const place = () => {
      const ctrl = root.querySelector('.react-flow__controls');
      const rail = root.querySelector('.canvas-tools-rail');
      const btn = ctrl?.querySelector('.react-flow__controls-button');
      if (!ctrl || !rail || !btn) return;
      // Medido en pantalla: React Flow ubica sus paneles con margen, no con bottom.
      const box = root.getBoundingClientRect();
      const r = ctrl.getBoundingClientRect();
      const bottomGap = box.bottom - r.bottom;
      rail.style.setProperty('--rail-bottom', `${Math.round(bottomGap + r.height + 10)}px`);
      rail.style.setProperty('--rail-left', `${Math.round(r.left - box.left)}px`);
      rail.style.setProperty('--rail-w', `${ctrl.offsetWidth}px`);
      rail.style.setProperty('--rail-btn', `${btn.offsetWidth}px`);
      rail.style.setProperty('--rail-btn-h', `${btn.offsetHeight}px`);
    };
    const attach = () => {
      const ctrl = root.querySelector('.react-flow__controls');
      if (!ctrl) return false;
      place();
      ro = new ResizeObserver(place);
      ro.observe(ctrl);
      return true;
    };
    let tries = 0;
    const t = setInterval(() => { tries += 1; if (attach() || tries > 20) clearInterval(t); }, 100);
    return () => { clearInterval(t); if (ro) ro.disconnect(); };
  }, []);

  const fittedRef = useRef(null);
  useEffect(() => {
    if (!reactFlowInstance || !currentProjectId || !nodes.length) return;
    if (fittedRef.current === currentProjectId) return;
    fittedRef.current = currentProjectId;
    requestAnimationFrame(() => {
      reactFlowInstance.fitView({ padding: 0.2, duration: 400, minZoom: 0.02 });
    });
  }, [reactFlowInstance, currentProjectId, nodes.length]);

  const displayedNodes = useMemo(() => {
    const hidden = revealActive ? new Set(hiddenNodeIds) : null;
    const involvedSet = new Set(batchInvolved);
    if (!batchMode && !hidden && !chain && !flashNodeId) return nodes;
    return nodes.map((n) => {
      let next = n;
      const cls = [];
      if (batchMode) {
        if (n.id === batchStart) cls.push('batch-node-start');
        else if (n.id === batchEnd) cls.push('batch-node-end');
        else if (involvedSet.has(n.id)) cls.push('batch-node-involved');
      }
      if (chain) {
        if (n.id === chain.nodeId) cls.push('path-selected');
        else if (chain.upstream.has(n.id)) cls.push('path-upstream');
        else if (chain.downstream.has(n.id)) cls.push('path-downstream');
        else cls.push('path-dimmed');
      }
      if (n.id === flashNodeId) cls.push('node-flash');
      if (cls.length) next = { ...next, className: cls.join(' ') };
      // Revelado didáctico: SÓLO visual, el nodo real ya existe en el store.
      if (hidden && hidden.has(n.id)) next = { ...next, hidden: true };
      return next;
    });
  }, [nodes, batchMode, batchStart, batchEnd, batchInvolved, revealActive,
      hiddenNodeIds, chain, flashNodeId]);

  const displayedEdges = useMemo(() => {
    const hidden = revealActive && hiddenEdgeIds.length ? new Set(hiddenEdgeIds) : null;
    if (!hidden && !chain) return edges;
    return edges.map((e) => {
      let next = e;
      if (chain) {
        const cls = chain.upstreamEdges.has(e.id)
          ? 'path-upstream'
          : chain.downstreamEdges.has(e.id)
            ? 'path-downstream'
            : 'path-dimmed';
        next = { ...next, className: cls };
      }
      if (hidden && hidden.has(e.id)) next = { ...next, hidden: true };
      return next;
    });
  }, [edges, revealActive, hiddenEdgeIds, chain]);

  // ── Envío LMX-T · secciones del canvas ──
  const sections = useSectionsStore((s) => s.sections);
  useEffect(() => {
    useSectionsStore.getState().load(currentProjectId);
    const reload = (e) => {
      const pid = e?.detail?.projectId;
      const cur = useWorkflowStore.getState().currentProjectId;
      if (!pid || pid === cur) useSectionsStore.getState().load(cur);
    };
    window.addEventListener('sml:sections-changed', reload);
    return () => window.removeEventListener('sml:sections-changed', reload);
  }, [currentProjectId]);
  const sectionNodes = useMemo(() => sectionsToNodes(sections), [sections]);
  const allNodes = useMemo(
    () => (sectionNodes.length ? [...sectionNodes, ...displayedNodes] : displayedNodes),
    [sectionNodes, displayedNodes]);
  // Los cambios de las secciones (mover) se guardan aparte; el resto sigue al
  // store del flujo como siempre.
  const handleNodesChange = useCallback((changes) => {
    const { sections: sc, flow } = splitChanges(changes);
    if (sc.length) applySectionMoves(sc);
    if (flow.length) onNodesChange(flow);
  }, [onNodesChange]);
  // Varios nodos seleccionados → «Marcar sección» los encierra en un recuadro.
  const selectedFlowNodes = useMemo(() => nodes.filter((n) => n.selected), [nodes]);
  const markSection = useCallback(async () => {
    const box = boxAround(selectedFlowNodes);
    if (!box) return;
    try {
      await useSectionsStore.getState().create(box, `Sección ${sections.length + 1}`);
    } catch (e) {
      import('sonner').then(({ toast }) => toast.error(e.message));
    }
  }, [sections.length, selectedFlowNodes]);
  // Ir a una sección (desde la lista de trabajos de LMX).
  useEffect(() => {
    const onFocus = (e) => {
      const sec = useSectionsStore.getState().sections.find((x) => x.id === e?.detail?.sectionId);
      if (!sec || !reactFlowInstance) return;
      reactFlowInstance.fitBounds({ x: sec.x, y: sec.y, width: sec.w, height: sec.h },
        { padding: 0.15, duration: 500 });
    };
    window.addEventListener('sml:focus-section', onFocus);
    return () => window.removeEventListener('sml:focus-section', onFocus);
  }, [reactFlowInstance]);

  const onInit = useCallback((instance) => {
    setReactFlowInstance(instance);
  }, []);

  const onNodeClick = useCallback((event, node) => {
    if (isSectionId(node?.id)) return; // un recuadro de sección no abre propiedades
    // Batch pickingMode: capture this click as a start/end marker instead
    // of opening the node properties panel. Only stops here if a picking
    // mode is active — otherwise fall through to normal selection.
    const bs = useBatchStore.getState();
    if (bs.pickingMode === 'start') {
      if (node.type !== 'uploadImage') {
        // Reject visually via toast (dynamic import to avoid circular dep).
        import('sonner').then(({ toast }) => toast.error('INICIO debe ser un nodo Upload Image.'));
        return;
      }
      bs.setStartNodeId(node.id);
      return;
    }
    if (bs.pickingMode === 'end') {
      bs.setEndNodeId(node.id);
      return;
    }
    setSelectedNode(node);
  }, [setSelectedNode]);

  const onPaneClick = useCallback(() => {
    setSelectedNode(null);
  }, [setSelectedNode]);

  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    (event) => {
      event.preventDefault();

      const type = event.dataTransfer.getData('application/reactflow');
      
      if (!type || !reactFlowInstance) return;

      const position = reactFlowInstance.screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });

      addNode(type, position);
    },
    [reactFlowInstance, addNode]
  );

  // Use the store's validation function
  const isValidConnection = useCallback((connection) => {
    return validateConnection(connection);
  }, [validateConnection]);

  const defaultEdgeOptions = {
    animated: true,
    style: { 
      stroke: '#10b981', 
      strokeWidth: 2,
    },
    type: 'smoothstep',
  };

  return (
    <div className={`canvas-container ${pickingMode ? `batch-picking batch-picking-${pickingMode}` : ''}`} ref={reactFlowWrapper} data-testid="canvas">
      <CanvasBackground />
      <ReactFlow
        nodes={allNodes}
        edges={displayedEdges}
        onNodesChange={handleNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onInit={onInit}
        onNodeClick={onNodeClick}
        onPaneClick={onPaneClick}
        onDragOver={onDragOver}
        onDrop={onDrop}
        nodeTypes={flowNodeTypes}
        isValidConnection={isValidConnection}
        connectionMode={ConnectionMode.Loose}
        minZoom={0.02}
        nodeDragThreshold={2}
        fitView
        snapToGrid
        snapGrid={[15, 15]}
        defaultEdgeOptions={defaultEdgeOptions}
        connectionLineStyle={{ 
          stroke: '#10b981', 
          strokeWidth: 2,
          strokeDasharray: '5 5',
        }}
        connectionLineType="smoothstep"
        deleteKeyCode={['Backspace', 'Delete']}
        selectionKeyCode={['Shift']}
        multiSelectionKeyCode={['Meta', 'Control']}
      >
        <Background
          variant="dots"
          gap={20}
          size={1}
          color="rgba(16, 185, 129, 0.15)"
        />
        <Controls
          className="canvas-controls"
          showZoom={true}
          showFitView={true}
          showInteractive={false}
        />
        {/* Envío LMX-8a · "Fondo" junto a los controles (esquina inferior
            izquierda, en L con «encuadrar»). Antes flotaba arriba a la derecha. */}
        {/* I-48 · panel de funciones encima del zoom, con la misma estética:
            de abajo hacia arriba Fondo dinámico y Galería. Crece hacia arriba
            a medida que se sumen funciones. */}
        {LAYOUT_V2 && (
          <Panel position="bottom-left" className="canvas-tools-rail" data-testid="v2-canvas-tools">
            {/* Envío PJ-2 · Biblioteca: tus marcas y personajes */}
            <button
              type="button"
              className="canvas-tool-btn"
              onClick={() => window.dispatchEvent(new CustomEvent('sml:open', { detail: { target: 'biblioteca' } }))}
              aria-label="Biblioteca"
              data-testid="canvas-library-btn"
            >
              <Library size={14} />
              <span className="canvas-tool-label">Biblioteca</span>
            </button>
            <button
              type="button"
              className="canvas-tool-btn"
              onClick={() => window.dispatchEvent(new CustomEvent('sml:open', { detail: { target: 'galeria' } }))}
              aria-label="Galería"
              data-testid="canvas-gallery-btn"
            >
              <Images size={14} />
              <span className="canvas-tool-label">Galería</span>
            </button>
            <CanvasBackgroundSelector placement="rail" />
          </Panel>
        )}
        <MiniMap
          className="canvas-minimap"
          pannable
          zoomable
          onClick={(event, position) => {
            if (position) reactFlowInstance?.setCenter(position.x, position.y, { duration: 400 });
          }}
          nodeColor={(node) => {
            switch (node.type) {
              case 'canvasSection': return 'rgba(255, 255, 255, 0.06)';
              case 'uploadImage': return '#3b82f6';
              case 'uploadVideo': return '#a855f7';
              case 'uploadAudio': return '#14b8a6';
              case 'imageEnhancement': return '#10b981';
              case 'videoGeneration': return '#8b5cf6';
              case 'videoWithSound': return '#06b6d4';
              case 'fabricAvatar': return '#f97316';
              case 'klingAvatar': return '#a855f7';
              case 'dreamActor': return '#ef4444';
              case 'nanoBanana': return '#f59e0b';
              case 'bgRemoval': return '#ec4899';
              case 'lyria2': return '#6366f1';
              case 'styleTransfer': return '#8b5cf6';
              case 'upscale': return '#eab308';
              case 'outputPreview': return '#8b5cf6';
              default: return '#6b7280';
            }
          }}
          maskColor="rgba(0, 0, 0, 0.8)"
        />
      </ReactFlow>
      
      {/* Empty state con plantillas DESACTIVADO (limpieza previa al rediseño
          de entrada): el componente sigue intacto, sólo no se monta. Para
          reactivarlo, descomentar la línea de abajo.
      {nodes.length === 0 && !revealActive && <EmptyCanvasTemplates />}
      */}

      {/* E5 · barra de navegación: buscador y caminos iluminados */}
      {nodes.length > 0 && (
        <div className="canvas-nav-bar" data-testid="canvas-nav-bar">
          <button
            type="button"
            className="canvas-nav-btn"
            onClick={() => setSearchOpen(true)}
            title="Buscar un nodo por número o nombre"
            data-testid="canvas-search-btn"
          >
            <Search size={13} />
            Buscar nodo
            <span className="canvas-nav-kbd">Ctrl K</span>
          </button>
          {selectedFlowNodes.length >= 2 && (
            <button
              type="button"
              className="canvas-nav-btn"
              onClick={markSection}
              title="Encerrar los nodos seleccionados en un recuadro con nombre"
              data-testid="canvas-mark-section"
            >
              <SquareDashed size={13} />
              Marcar sección
            </button>
          )}
          {PATH_HIGHLIGHT_AVAILABLE && (
            <button
              type="button"
              className={`canvas-nav-btn ${pathHighlight ? 'active' : ''}`}
              onClick={togglePathHighlight}
              title="Iluminar la cadena del nodo seleccionado (Esc para salir)"
              data-testid="canvas-path-highlight-toggle"
            >
              <Route size={13} />
              Caminos
            </button>
          )}
        </div>
      )}

      {/* Buscador Ctrl+K (vive dentro del provider para poder centrar) */}
      <NodeSearchPalette />

      {/* Run MCP en curso: badge, estados por nodo y botón de detener */}
      <McpRunOverlay />

      {/* Aparición didáctica del workflow (ghost desde el panel + conexiones) */}
      <WorkflowRevealOverlay />
    </div>
  );
});

Canvas.displayName = 'Canvas';

const CanvasWrapper = () => (
  <ReactFlowProvider>
    <Canvas />
    <SourcePickerModal />
    <GallerySelectModal />
  </ReactFlowProvider>
);

export default CanvasWrapper;
